import { removeUndefinedValues } from "../utils/firestore";
import { CollectionPaths } from "@order-up/common";
import { ITaxRate, TaxRate } from "@order-up/models";
import { TaxRateSchema } from "@order-up/schemas";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { counterService } from "./counters";
import { resolveLocationDocId, resolveTenantDocId } from "./resolveDocIds";

const logger = createLogger("TaxRateService");

/**
 * Gets all tax rates for a restaurant
 */
const getTaxRatesByRestaurantId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<TaxRate[]> => {
  logger.debug(
    `Getting tax rates for tenantId: ${tenantId}, restaurantId: ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      logger.warn(`Tenant with ID ${tenantId} not found`);
      return [];
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      logger.warn(
        `Restaurant with ID ${locationNumericId} not found in tenant ${tenantId}`,
      );
      return [];
    }

    const collectionPath = CollectionPaths.taxRates(
      tenantDocId,
      locationDocId,
    );
    const taxRateRepo = new FirestoreRepository<TaxRate>(
      collectionPath,
      TaxRate,
    );
    const taxRates = await taxRateRepo.find();

    logger.debug(
      `Retrieved ${taxRates.length} tax rates for restaurant ${locationNumericId}`,
    );
    return taxRates;
  } catch (error) {
    logger.error(`Error fetching tax rates for restaurant ${locationNumericId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Gets a tax rate by its business ID
 */
const getTaxRateById = async (
  tenantId: number,
  locationNumericId: number,
  taxRateId: number,
): Promise<TaxRate | null> => {
  logger.debug(`Getting tax rate ${taxRateId} for restaurant ${locationNumericId}`);

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) return null;

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) return null;

    const collectionPath = CollectionPaths.taxRates(
      tenantDocId,
      locationDocId,
    );
    const taxRateRepo = new FirestoreRepository<TaxRate>(
      collectionPath,
      TaxRate,
    );
    const results = await taxRateRepo.find([
      { field: "taxRateId", operator: "==", value: taxRateId },
    ]);

    return results.length > 0 ? results[0] : null;
  } catch (error) {
    logger.error(`Error fetching tax rate ${taxRateId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates multiple tax rates in a batch operation using Firestore writeBatch()
 */
const createTaxRatesBatch = async (
  tenantId: number,
  locationNumericId: number,
  taxRatesData: ITaxRate[],
): Promise<DocumentWithId<TaxRate>[]> => {
  logger.debug(
    `Creating ${taxRatesData.length} tax rates in batch for restaurant ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.taxRates(
      tenantDocId,
      locationDocId,
    );
    const taxRateRepo = new FirestoreRepository<TaxRate>(
      collectionPath,
      TaxRate,
    );

    // Step 1: Generate all IDs and validate all data first
    const validatedPayloads: any[] = [];

    for (const taxRateData of taxRatesData) {
      // Use provided IDs if they exist, otherwise generate new ones
      let taxRateId: number;
      let guid: string;

      if (taxRateData.taxRateId && taxRateData.guid) {
        // Use provided IDs from mock data to maintain relationships
        taxRateId = taxRateData.taxRateId;
        guid = taxRateData.guid;
        logger.debug(
          `Using provided IDs for: ${taxRateData.name} (ID: ${taxRateId})`,
        );
      } else {
        // Generate new IDs when not provided
        const generated = await counterService.generateTaxRateId(
          tenantId,
          locationNumericId,
        );
        taxRateId = generated.taxRateId;
        guid = generated.guid;
        logger.debug(
          `Generated new IDs for: ${taxRateData.name} (ID: ${taxRateId})`,
        );
      }

      // Prepare tax rate data with generated IDs for validation
      const taxRateForValidation = {
        ...taxRateData,
        taxRateId,
        guid,
      };

      // Validate the data using the schema (without server timestamps)
      TaxRateSchema.parse(taxRateForValidation);

      // Create tax rate payload (server timestamps will be added by batchCreate)
      const taxRatePayload: any = removeUndefinedValues(taxRateForValidation);
      validatedPayloads.push(taxRatePayload);
    }

    logger.debug(
      `All ${validatedPayloads.length} tax rates validated, executing batch create`,
    );

    // Step 2: Batch create all documents using Firestore writeBatch()
    const taxRates = await taxRateRepo.batchCreate(validatedPayloads);

    logger.debug(`Successfully created ${taxRates.length} tax rates in batch`);
    return taxRates;
  } catch (error) {
    logger.error(`Error creating tax rates in batch`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Checks if a tax rate name already exists (case-insensitive)
 */
const isNameTaken = async (
  tenantId: number,
  locationNumericId: number,
  name: string,
  excludeId?: number,
): Promise<{ taken: boolean; isArchived?: boolean }> => {
  const normalizedName = name.trim().toLowerCase();
  const rates = await getTaxRatesByRestaurantId(tenantId, locationNumericId);
  const conflict = rates.find((rate) => {
    if (rate.name.toLowerCase() !== normalizedName) return false;
    if (excludeId !== undefined) {
      return String(rate.taxRateId) !== String(excludeId);
    }
    return true;
  });

  return {
    taken: !!conflict,
    isArchived: conflict ? conflict.disabled === true : undefined,
  };
};

/**
 * Updates an existing tax rate
 */
const updateTaxRate = async (
  tenantId: number,
  locationNumericId: number,
  taxRateId: number,
  taxRateData: Partial<ITaxRate>,
): Promise<DocumentWithId<TaxRate>> => {
  logger.debug(
    `Updating tax rate ${taxRateId} for restaurant ${locationNumericId}:`,
    taxRateData,
  );

  try {
    const existingTaxRate = await getTaxRateById(
      tenantId,
      locationNumericId,
      taxRateId,
    );
    if (!existingTaxRate) {
      throw new Error(`Tax rate with ID ${taxRateId} not found`);
    }

    if (
      taxRateData.name &&
      taxRateData.name.toLowerCase() !== existingTaxRate.name.toLowerCase()
    ) {
      const conflict = await isNameTaken(
        tenantId,
        locationNumericId,
        taxRateData.name,
        taxRateId,
      );
      if (conflict.taken) {
        const message = conflict.isArchived
          ? `A tax rate with name "${taxRateData.name}" already exists but is archived`
          : `A tax rate with name "${taxRateData.name}" already exists`;
        throw new Error(message);
      }
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.taxRates(
      tenantDocId!,
      locationDocId!,
    );
    const taxRateRepo = new FirestoreRepository<TaxRate>(
      collectionPath,
      TaxRate,
    );

    const updatePayload = removeUndefinedValues({
      ...taxRateData,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    await taxRateRepo.updateById((existingTaxRate as any).docId, updatePayload);

    const updatedTaxRate = {
      ...existingTaxRate,
      ...taxRateData,
    } as DocumentWithId<TaxRate>;
    return updatedTaxRate;
  } catch (error) {
    logger.error(`Error updating tax rate ${taxRateId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Deletes a tax rate
 */
const deleteTaxRate = async (
  tenantId: number,
  locationNumericId: number,
  taxRateId: number,
): Promise<void> => {
  logger.debug(`Deleting tax rate ${taxRateId} for restaurant ${locationNumericId}`);

  try {
    const existingTaxRate = await getTaxRateById(
      tenantId,
      locationNumericId,
      taxRateId,
    );
    if (!existingTaxRate) {
      throw new Error(`Tax rate with ID ${taxRateId} not found`);
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.taxRates(
      tenantDocId!,
      locationDocId!,
    );
    const taxRateRepo = new FirestoreRepository<TaxRate>(
      collectionPath,
      TaxRate,
    );
    await taxRateRepo.deleteById((existingTaxRate as any).docId);
  } catch (error) {
    logger.error(`Error deleting tax rate ${taxRateId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Gets the default tax rate for a restaurant
 * Returns the tax rate marked as default and not disabled
 */
const getDefaultTaxRate = async (
  tenantId: number,
  locationNumericId: number,
): Promise<TaxRate | null> => {
  logger.debug(
    `Getting default tax rate for tenantId: ${tenantId}, restaurantId: ${locationNumericId}`,
  );

  try {
    const taxRates = await getTaxRatesByRestaurantId(tenantId, locationNumericId);

    // Find the default tax rate that is not disabled
    const defaultTaxRate = taxRates.find((tr) => tr.default && !tr.disabled);

    if (!defaultTaxRate) {
      logger.debug(`No default tax rate found for restaurant ${locationNumericId}`);
      return null;
    }

    logger.debug(`Found default tax rate: ${defaultTaxRate.name}`, {
      taxRateId: defaultTaxRate.taxRateId,
      restaurantId: locationNumericId,
    });
    return defaultTaxRate;
  } catch (error) {
    logger.error(`Error fetching default tax rate for restaurant ${locationNumericId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const taxRateService = {
  getTaxRatesByRestaurantId,
  getTaxRateById,
  createTaxRatesBatch,
  updateTaxRate,
  deleteTaxRate,
  isNameTaken,
  getDefaultTaxRate,
};
